import { line, curveLinear } from 'd3-shape';

/**
 * Render the static parcel ascent: the parcel TEMPERATURE curve (solid pink)
 * for the full ascent, plus the parcel DEWPOINT curve as an isohume from the
 * surface dewpoint up to the LCL where the two meet. The latter is what
 * Skysight draws as the diagonal "to-LCL" line from ~surface-dewpoint to the
 * LCL marker, giving the classic Bolton-style LCL construction.
 *
 * Class "skewt-ext-parcel" — solid pink (see skewt.css).
 * Class "skewt-ext-parcel-dewpoint" — same color, dashed.
 *
 * @param {import('d3-selection').Selection} container
 * @param {import('../skewt/scales.js').Scales} scales
 * @param {Array<{t,p}>} parcelPath - from liftParcel
 * @param {{plcl:number, tlcl:number, surfaceTd:number, surfacePress:number}} [lcl]
 */
export function drawParcelLine(container, scales, parcelPath, lcl) {
    container.selectAll('.skewt-ext-parcel').remove();
    container.selectAll('.skewt-ext-parcel-dewpoint').remove();
    if (!parcelPath || parcelPath.length < 2) return;

    const pathGen = line()
        .x((d) => scales.skewX(d.t, d.p))
        .y((d) => scales.y(d.p))
        .curve(curveLinear);

    container
        .append('path')
        .attr('class', 'skewt-ext-parcel')
        .attr('d', pathGen(parcelPath));

    // Parcel-dewpoint isohume from surface Td up to LCL.
    if (
        lcl &&
        Number.isFinite(lcl.plcl) &&
        Number.isFinite(lcl.tlcl) &&
        Number.isFinite(lcl.surfaceTd) &&
        Number.isFinite(lcl.surfacePress)
    ) {
        // Bolton saturation vapor pressure (hPa)
        const es = (T) => 6.112 * Math.exp((17.67 * T) / (T + 243.5));
        const tdFromEs = (e) => {
            const ln = Math.log(e / 6.112);
            return (243.5 * ln) / (17.67 - ln);
        };
        const isohumeT = (Td0, p0, p) => tdFromEs(es(Td0) * (p / p0));

        const STEP = 5;
        const pts = [];
        for (let p = lcl.surfacePress; p >= lcl.plcl; p -= STEP) {
            pts.push({ t: isohumeT(lcl.surfaceTd, lcl.surfacePress, p), p });
        }
        // Anchor at LCL exactly
        pts.push({ t: lcl.tlcl, p: lcl.plcl });

        container
            .append('path')
            .attr('class', 'skewt-ext-parcel-dewpoint')
            .attr('d', pathGen(pts));
    }
}
